export default class Misc {
    constructor() {
        // Shared billing period (maand / jaar) state — read by initPeriod,
        // initConfigurator and initLicense.
        this.period = "month";
    }

    init() {
        this.initSliders();
        this.initNav();
        this.initShare();
        this.initPeriod();
        this.initConfigurator();
        this.initLicense();
        this.initQtySteppers();
        this.initOrderBuilder();
        this.initConfigWizard();
    }

    isYear() {
        return this.period === "year";
    }

    // -----------------------------------------------------------------------
    // Scroll-snap slider
    //
    // Progressive enhancement for any [data-slider] block (see
    // templates/components/partner-slider.twig). The track scrolls natively
    // (touch swipe / trackpad work for free); this only wires the arrow
    // buttons and the progress bar to that scroll position. Works for
    // multiple sliders on a page.
    // -----------------------------------------------------------------------
    initSliders() {
        document.querySelectorAll("[data-slider]").forEach((slider) => {
            const track = slider.querySelector("[data-slider-track]");
            if (!track) {
                return;
            }

            const prev = slider.querySelector("[data-slider-prev]");
            const next = slider.querySelector("[data-slider-next]");
            const progress = slider.querySelector("[data-slider-progress]");

            const maxScroll = () => track.scrollWidth - track.clientWidth;

            // Scroll roughly one viewport of logos per click.
            const step = () => Math.max(track.clientWidth * 0.8, 200);

            const update = () => {
                const max = maxScroll();
                const ratio = max > 0 ? track.scrollLeft / max : 0;

                if (progress) {
                    progress.style.width = `${Math.min(100, ratio * 100)}%`;
                }
                if (prev) {
                    prev.disabled = track.scrollLeft <= 1;
                }
                if (next) {
                    next.disabled = max <= 0 || track.scrollLeft >= max - 1;
                }
            };

            if (prev) {
                prev.addEventListener("click", () => {
                    track.scrollBy({ left: -step(), behavior: "smooth" });
                });
            }
            if (next) {
                next.addEventListener("click", () => {
                    track.scrollBy({ left: step(), behavior: "smooth" });
                });
            }

            track.addEventListener("scroll", update, { passive: true });
            window.addEventListener("resize", update);

            // The track's scrollWidth grows as logos finish loading, so the
            // initial measurement runs too early. Re-measure whenever the
            // content changes.
            track.querySelectorAll("img").forEach((img) => {
                if (!img.complete) {
                    img.addEventListener("load", update, { once: true });
                }
            });
            window.addEventListener("load", update);
            if ("ResizeObserver" in window) {
                new ResizeObserver(update).observe(track);
            }

            update();
        });
    }

    // -----------------------------------------------------------------------
    // Navigation mega-menu
    //
    // Plain links keep the bar compact; only links with a [data-nav-trigger]
    // open a matching [data-nav-panel], which grows the fixed bar. Hover/focus
    // opens, leaving the nav (or Escape) closes. Also drives the mobile menu
    // toggle.
    // -----------------------------------------------------------------------
    initNav() {
        const nav = document.querySelector("[data-nav]");
        if (!nav) {
            return;
        }

        const triggers = nav.querySelectorAll("[data-nav-trigger]");
        const panels = nav.querySelectorAll("[data-nav-panel]");

        // openId: which panel is currently shown. pinned: a click locked it
        // open, so it survives mouseleave until an outside click / Escape /
        // re-click.
        let openId = null;
        let pinned = false;

        // On the homepage the nav overlays the hero transparently and only
        // turns solid (white) once scrolled past the top or when a mega-menu
        // is open. Other pages have data-overlay="false" and stay solid at
        // all times.
        const overlay = nav.dataset.overlay === "true";
        let mobileOpen = false;
        const updateSolid = () => {
            if (!overlay) {
                return;
            }
            nav.dataset.solid =
                window.scrollY > 20 || openId !== null || mobileOpen
                    ? "true"
                    : "false";
        };
        if (overlay) {
            window.addEventListener("scroll", updateSolid, { passive: true });
        }

        const show = (id) => {
            panels.forEach((panel) => {
                panel.dataset.open =
                    panel.getAttribute("data-nav-panel") === id
                        ? "true"
                        : "false";
            });
            triggers.forEach((trigger) => {
                trigger.setAttribute(
                    "aria-expanded",
                    trigger.getAttribute("data-nav-trigger") === id
                        ? "true"
                        : "false",
                );
            });
            openId = id;
            updateSolid();
        };

        const close = () => {
            panels.forEach((panel) => {
                panel.dataset.open = "false";
            });
            triggers.forEach((trigger) => {
                trigger.setAttribute("aria-expanded", "false");
            });
            openId = null;
            pinned = false;
            updateSolid();
        };

        triggers.forEach((trigger) => {
            const id = trigger.getAttribute("data-nav-trigger");
            // Hover / keyboard focus previews the panel (unless one is pinned
            // open).
            trigger.addEventListener("mouseenter", () => {
                if (!pinned) {
                    show(id);
                }
            });
            trigger.addEventListener("focus", () => {
                if (!pinned) {
                    show(id);
                }
            });
            // Click pins it open; clicking the open one again closes it.
            trigger.addEventListener("click", () => {
                if (pinned && openId === id) {
                    close();
                } else {
                    show(id);
                    pinned = true;
                }
            });
        });

        // Hovering a plain link collapses a previewed (unpinned) panel.
        nav.querySelectorAll("[data-nav-plain]").forEach((link) => {
            link.addEventListener("mouseenter", () => {
                if (!pinned) {
                    close();
                }
            });
        });

        // Leaving the nav closes a preview but leaves a pinned panel open.
        nav.addEventListener("mouseleave", () => {
            if (!pinned) {
                close();
            }
        });

        // A click outside the nav, or Escape, always closes (and unpins).
        document.addEventListener("click", (event) => {
            if (!nav.contains(event.target)) {
                close();
            }
        });
        document.addEventListener("keydown", (event) => {
            if (event.key === "Escape") {
                close();
            }
        });

        // Mobile menu toggle
        const toggle = nav.querySelector("[data-nav-toggle]");
        const mobile = nav.querySelector("[data-nav-mobile]");
        if (toggle && mobile) {
            toggle.addEventListener("click", () => {
                const willOpen = mobile.hidden;
                mobile.hidden = !willOpen;
                toggle.setAttribute("aria-expanded", String(willOpen));
                mobileOpen = willOpen;
                updateSolid();
            });
        }

        updateSolid();
    }

    // -----------------------------------------------------------------------
    // Share button
    //
    // Uses the Web Share API where available (mobile / modern browsers);
    // otherwise copies the current URL to the clipboard. Hooks any
    // [data-share] element.
    // -----------------------------------------------------------------------
    initShare() {
        document.querySelectorAll("[data-share]").forEach((button) => {
            button.addEventListener("click", async () => {
                const shareData = {
                    title:
                        button.getAttribute("data-share-title") ||
                        document.title,
                    url: window.location.href,
                };

                try {
                    if (navigator.share) {
                        await navigator.share(shareData);
                    } else if (navigator.clipboard) {
                        await navigator.clipboard.writeText(shareData.url);
                        button.dataset.copied = "true";
                        window.setTimeout(() => {
                            delete button.dataset.copied;
                        }, 2000);
                    }
                } catch (error) {
                    // User cancelled the share sheet — nothing to do.
                }
            });
        });
    }

    // -----------------------------------------------------------------------
    // Billing period (maand / jaar) — shared buy-box state
    //
    // The toggle updates the hidden inputs the form posts (isource_period),
    // the headline price, the price suffix, and tells the configurator /
    // license selectors to recompute via the "isource:period" event. Preview
    // only — the authoritative price is recalculated server-side.
    // -----------------------------------------------------------------------
    initPeriod() {
        const toggle = document.querySelector("[data-period-toggle]");
        const suffixEls = document.querySelectorAll("[data-period-suffix]");
        const priceEls = document.querySelectorAll("[data-price-display]");
        const inputs = document.querySelectorAll("[data-period-input]");

        // Initialise the shared period from the hidden input — covers
        // year-only / month-only products that render no toggle.
        const initInput = document.querySelector("[data-period-input]");
        if (initInput && initInput.value) {
            this.period = initInput.value === "year" ? "year" : "month";
        }

        const apply = (period) => {
            this.period = period === "year" ? "year" : "month";
            inputs.forEach((i) => {
                i.value = this.period;
            });
            suffixEls.forEach((s) => {
                s.textContent = this.isYear() ? "per jaar" : "per maand";
            });
            priceEls.forEach((el) => {
                const html = this.isYear()
                    ? el.dataset.priceYear
                    : el.dataset.priceMonth;
                if (html) {
                    el.innerHTML = html;
                }
            });
            document.dispatchEvent(
                new CustomEvent("isource:period", {
                    detail: { period: this.period },
                }),
            );
        };

        if (!toggle) {
            return;
        }
        const buttons = [...toggle.querySelectorAll("[data-period]")];
        buttons.forEach((btn) => {
            btn.addEventListener("click", () => {
                buttons.forEach((b) => {
                    b.dataset.selected = "false";
                });
                btn.dataset.selected = "true";
                apply(btn.dataset.period);
            });
        });
    }

    // -----------------------------------------------------------------------
    // Product configurator (live total, period-aware)
    //
    // Mirrors the server-side price for configurable products:
    //   total = base(period) + vCPU*cpu(period) + RAM*ram(period) + disk*disk(period)
    // Preview only — the authoritative price is recalculated in PHP
    // (ConfigurableProduct::setCartItemPrice).
    // -----------------------------------------------------------------------
    initConfigurator() {
        const root = document.querySelector("[data-configurator]");
        if (!root) {
            return;
        }

        const totalEl = document.querySelector("[data-config-total]");
        const groups = root.querySelectorAll("[data-config-group]");
        const groupByKey = (key) =>
            root.querySelector(`[data-config-group="${key}"]`);

        const fmt = (n) => n.toFixed(2).replace(".", ",");
        const euro = (n) => "€" + fmt(n);
        const factor = parseFloat(root.dataset.factor) || 1;
        const mode = root.dataset.mode || "month";
        const resMult = () => (this.isYear() ? factor : 1);
        const unitPrice = (group) => parseFloat(group.dataset.unitPrice) || 0;
        const qtyInput = document.querySelector(
            '[data-qty-input][name="quantity"]',
        );
        const qty = () => Math.max(1, parseInt(qtyInput?.value, 10) || 1);
        // Base: yearly as-is in year-only mode; otherwise monthly (× factor
        // for year).
        const baseForPeriod = () => {
            const b = parseFloat(root.dataset.base) || 0;
            if (mode === "year") return b;
            return this.isYear() ? b * factor : b;
        };

        const recalc = () => {
            let unit = baseForPeriod();
            groups.forEach((group) => {
                const input = group.querySelector("[data-config-input]");
                unit +=
                    (parseFloat(input?.value) || 0) *
                    unitPrice(group) *
                    resMult();
            });
            if (totalEl) {
                totalEl.textContent = euro(unit * qty());
            }
        };

        // Swap the per-group "€x / unit" labels to the active period (number
        // only — the currency symbol is rendered before the span in the
        // template).
        const refreshLabels = () => {
            groups.forEach((group) => {
                const label = group.querySelector("[data-group-price]");
                if (label) {
                    label.textContent = fmt(unitPrice(group) * resMult());
                }
            });
        };

        // Disable option tiles above `max`; snap the selection down if it
        // exceeds it.
        const constrain = (key, max) => {
            const group = groupByKey(key);
            if (!group) {
                return;
            }
            const input = group.querySelector("[data-config-input]");
            const tiles = [...group.querySelectorAll("[data-config-tile]")];
            tiles.forEach((t) => {
                t.dataset.disabled =
                    parseInt(t.dataset.value, 10) > max ? "true" : "false";
            });
            if (parseInt(input.value, 10) > max) {
                const allowed = tiles.filter(
                    (t) => parseInt(t.dataset.value, 10) <= max,
                );
                const best = allowed[allowed.length - 1];
                if (best) {
                    tiles.forEach((t) => {
                        t.dataset.selected = "false";
                    });
                    best.dataset.selected = "true";
                    input.value = best.dataset.value;
                }
            }
        };

        // Apply the selected CPU tier's RAM/storage ceilings.
        const applyCpuLimits = () => {
            const cpu = groupByKey("cpu");
            const selected = cpu?.querySelector(
                '[data-config-tile][data-selected="true"]',
            );
            if (!selected) {
                return;
            }
            if (selected.dataset.maxRam) {
                constrain("ram", parseInt(selected.dataset.maxRam, 10));
            }
            if (selected.dataset.maxDisk) {
                constrain("disk", parseInt(selected.dataset.maxDisk, 10));
            }
        };

        groups.forEach((group) => {
            const input = group.querySelector("[data-config-input]");
            const tiles = group.querySelectorAll("[data-config-tile]");
            tiles.forEach((tile) => {
                tile.addEventListener("click", () => {
                    if (tile.dataset.disabled === "true") {
                        return;
                    }
                    tiles.forEach((t) => {
                        t.dataset.selected = "false";
                    });
                    tile.dataset.selected = "true";
                    if (input) {
                        input.value = tile.dataset.value;
                    }
                    if (group.dataset.configGroup === "cpu") {
                        applyCpuLimits();
                    }
                    recalc();
                });
            });
        });

        document.addEventListener("isource:period", () => {
            refreshLabels();
            recalc();
        });
        qtyInput?.addEventListener("input", recalc);
        qtyInput?.addEventListener("change", recalc);

        applyCpuLimits();
        refreshLabels();
        recalc();
    }

    // -----------------------------------------------------------------------
    // License package selector (Microsoft 365: Basic / Business / Premium)
    // -----------------------------------------------------------------------
    initLicense() {
        const root = document.querySelector("[data-license]");
        if (!root) {
            return;
        }
        const input = root.querySelector("[data-license-input]");
        const totalEl = document.querySelector("[data-license-total]");
        const tiles = root.querySelectorAll("[data-license-tile]");
        const euro = (n) => "€" + n.toFixed(2).replace(".", ",");

        const factor = parseFloat(root.dataset.factor) || 1;
        const mode = root.dataset.mode || "month";
        const priceOf = (tile) => {
            const p = parseFloat(tile && tile.dataset.price) || 0;
            if (mode === "year") {
                return p;
            }
            return this.isYear() ? p * factor : p;
        };
        const selected = () =>
            root.querySelector('[data-license-tile][data-selected="true"]') ||
            tiles[0];

        // Headline price scales with the selected package (per unit); Totaal is
        // the per-unit price × the number of users (quantity).
        const priceDisplay = document.querySelector("[data-price-display]");
        const titlePackage = document.querySelector("[data-title-package]");
        const qtyInput = document.querySelector(
            '[data-qty-input][name="quantity"]',
        );
        const qty = () => Math.max(1, parseInt(qtyInput?.value, 10) || 1);

        const refresh = () => {
            const unit = priceOf(selected());
            if (priceDisplay) {
                priceDisplay.textContent = euro(unit);
            }
            if (totalEl) {
                totalEl.textContent = euro(unit * qty());
            }
        };

        qtyInput?.addEventListener("input", refresh);
        qtyInput?.addEventListener("change", refresh);

        // "Inhoud van het product": mark each feature included (blue check) or
        // excluded (grey dash) for the selected package. Each row carries
        // data-basic / data-business / data-premium = "1" | "0".
        const featureRows = document.querySelectorAll("[data-feature]");
        const updateFeatures = (pkg) => {
            featureRows.forEach((row) => {
                row.dataset.included =
                    row.dataset[pkg] === "1" ? "true" : "false";
            });
        };

        tiles.forEach((tile) => {
            tile.addEventListener("click", () => {
                tiles.forEach((t) => {
                    t.dataset.selected = "false";
                });
                tile.dataset.selected = "true";
                if (input) {
                    input.value = tile.dataset.value;
                }
                refresh();
                updateFeatures(tile.dataset.value);
                if (titlePackage) {
                    titlePackage.textContent = tile.dataset.label;
                }
            });
        });

        document.addEventListener("isource:period", refresh);
        refresh();
        updateFeatures(selected().dataset.value);
        if (titlePackage) {
            titlePackage.textContent = selected().dataset.label;
        }
    }

    // -----------------------------------------------------------------------
    // Quantity steppers ( − [n] + )
    // -----------------------------------------------------------------------
    initQtySteppers() {
        document.querySelectorAll("[data-qty]").forEach((stepper) => {
            const input = stepper.querySelector("[data-qty-input]");
            if (!input) {
                return;
            }
            // Respect the input's own min/max (cart allows min 0; note `|| 1`
            // would wrongly turn a valid 0 into 1). Max is optional.
            const min = Number.isFinite(parseInt(input.min, 10))
                ? parseInt(input.min, 10)
                : 1;
            const max = Number.isFinite(parseInt(input.max, 10))
                ? parseInt(input.max, 10)
                : Infinity;
            const step = (delta) => {
                const current = Number.isFinite(parseInt(input.value, 10))
                    ? parseInt(input.value, 10)
                    : min;
                input.value = Math.min(max, Math.max(min, current + delta));
                input.dispatchEvent(new Event("change", { bubbles: true }));
            };
            stepper
                .querySelector("[data-qty-dec]")
                ?.addEventListener("click", () => step(-1));
            stepper
                .querySelector("[data-qty-inc]")
                ?.addEventListener("click", () => step(1));
        });
    }

    // -----------------------------------------------------------------------
    // Related-product order builder
    //
    // When the buy-box form is submitted with related quantities set, add the
    // main product AND each selected related product to the cart via
    // WooCommerce AJAX, then go to the cart. With no related quantities (or
    // no AJAX endpoint) the form submits normally and only the main product
    // is added. The main product keeps its full configuration; related
    // products use their default config + the same billing period.
    // -----------------------------------------------------------------------
    initOrderBuilder() {
        const form = document.querySelector("[data-main-form]");
        if (!form) {
            return;
        }

        const ajaxUrl = form.dataset.ajaxUrl;
        const cartUrl = form.dataset.cartUrl || "/cart/";

        form.addEventListener("submit", (e) => {
            const related = [
                ...document.querySelectorAll("[data-related-item]"),
            ]
                .map((row) => ({
                    id: row.dataset.productId,
                    qty:
                        parseInt(
                            row.querySelector("[data-related-qty]")?.value,
                            10,
                        ) || 0,
                }))
                .filter((r) => r.id && r.qty > 0);

            if (!related.length || !ajaxUrl) {
                return; // native submit → main product only
            }

            e.preventDefault();
            const period =
                form.querySelector("[data-period-input]")?.value || "month";
            const mainId = form.querySelector('[name="add-to-cart"]')?.value;
            const add = (body) =>
                fetch(ajaxUrl, {
                    method: "POST",
                    body,
                    credentials: "same-origin",
                });

            (async () => {
                try {
                    // Main product, with its full configuration (serialized
                    // form).
                    const mainBody = new FormData(form);
                    if (mainId) {
                        mainBody.append("product_id", mainId);
                        mainBody.append("add-to-cart", mainId);
                    }
                    await add(mainBody);

                    // Each selected related product (default config, same
                    // period).
                    for (const r of related) {
                        const body = new FormData();
                        body.append("product_id", r.id);
                        body.append("add-to-cart", r.id);
                        body.append("quantity", String(r.qty));
                        body.append("isource_period", period);
                        await add(body);
                    }
                    window.location.href = cartUrl;
                } catch (err) {
                    form.submit(); // fallback: at least add the main product
                }
            })();
        });
    }

    // -----------------------------------------------------------------------
    // Server configurator wizard (Licenties → Configuratie → Facturatie)
    //
    // Adds step navigation and the step-3 price/summary preview on top of the
    // existing configurator primitives (tile selection + CPU limits come from
    // initConfigurator(), the qty stepper from initQtySteppers(), the period
    // toggle from initPeriod()) — this method doesn't duplicate that logic,
    // it only reacts to it. Preview only — price is authoritative server-side.
    // -----------------------------------------------------------------------
    initConfigWizard() {
        const root = document.querySelector("[data-config-wizard]");
        if (!root) {
            return;
        }

        const steps = [...root.querySelectorAll("[data-step]")];
        const panels = [...root.querySelectorAll("[data-panel]")];
        const prevBtn = root.querySelector("[data-wizard-prev]");
        const nextBtn = root.querySelector("[data-wizard-next]");
        const qtyInput = root.querySelector("[data-qty-input]");
        const groups = [...root.querySelectorAll("[data-config-group]")];

        const fmt = (n) => n.toFixed(2).replace(".", ",");
        const currency = root.dataset.currency || "€";
        const euro = (n) => currency + fmt(n);
        const factor = parseFloat(root.dataset.factor) || 1;
        const mode = root.dataset.mode || "month";
        const base = parseFloat(root.dataset.base) || 0;
        // Preview only, matches the "BTW 21%" shown elsewhere on this
        // template — the authoritative rate/amount comes from WooCommerce at
        // checkout.
        const vatRate = 0.21;

        const qty = () => parseInt(qtyInput?.value, 10) || 1;
        const isWindows365 = root.dataset.type === "windows365";

        const selectedTile = (group) =>
            group.querySelector('[data-config-tile][data-selected="true"]');
        const groupByKey = (key) =>
            root.querySelector(`[data-config-group="${key}"]`);
        const bundleTiles = [...root.querySelectorAll("[data-bundle-tile]")];
        const selectedBundle = () =>
            bundleTiles.find((t) => t.dataset.selected === "true");

        // Per-license price for a given period, split into its two parts
        // (mirrors ConfigurableProduct::calculatePrice — qty multiplies the
        // sum, it isn't baked in):
        //   licensePart = the product's own base price for the period
        //   configPart  = the configured CPU/RAM/disk resources for the period
        //                 (Windows 365: the selected bundle's all-inclusive
        //                 price — licensePart is always 0, see class docblock).
        const licensePart = (isYear) =>
            mode === "year" ? base : isYear ? base * factor : base;

        const configPart = (isYear) => {
            const resMult = isYear ? factor : 1;
            if (isWindows365) {
                const price = parseFloat(selectedBundle()?.dataset.price) || 0;
                return price * resMult;
            }
            return groups.reduce((sum, group) => {
                const unit = parseFloat(group.dataset.unitPrice) || 0;
                const value =
                    parseFloat(
                        group.querySelector("[data-config-input]")?.value,
                    ) || 0;
                return sum + value * unit * resMult;
            }, 0);
        };

        // Cloud Desktop: the configured resources are a single shared cost for
        // the whole cart line — dividing by qty here cancels out the × qty
        // WooCommerce applies to the unit price, so it doesn't scale with the
        // number of licenses. Windows 365: each license carries its own
        // configuration, so the resources cost isn't divided (mirrors
        // ConfigurableProduct::setCartItemPrice).
        const perLicense = (isYear) =>
            isWindows365
                ? licensePart(isYear) + configPart(isYear)
                : licensePart(isYear) + configPart(isYear) / qty();

        const configSummary = () => {
            if (isWindows365) {
                const cpu = selectedTile(groupByKey("cpu"))?.textContent.trim();
                const bundle = selectedBundle()?.dataset.label;
                return [cpu, bundle].filter(Boolean).join(" · ");
            }
            return groups
                .map((group) => selectedTile(group)?.textContent.trim())
                .filter(Boolean)
                .join(" · ");
        };

        // Like configSummary(), but spelled out for the step-2 cost card
        // (e.g. "2 cores · 8 GB RAM · 64 GB").
        const configSpecs = () => {
            if (isWindows365) {
                return configSummary();
            }
            return groups
                .map((group) => {
                    const label = selectedTile(group)?.textContent.trim();
                    if (!label) return null;
                    return group.dataset.configGroup === "ram"
                        ? `${label} RAM`
                        : label;
                })
                .filter(Boolean)
                .join(" · ");
        };

        // Growing cost breakdown in the footer. Cloud Desktop: Licenties
        // always, Configuratie from step 2 onward (Licenties scales with the
        // number of licenses; Configuratie is a flat, shared cost and
        // doesn't). Windows 365 has no separate license cost — the bundle
        // price already IS the per-license price — so it's a single row.
        const buildBreakdown = () => {
            const el = root.querySelector("[data-breakdown]");
            if (!el) return;

            const isYear = this.isYear();
            const licenseMonthly = licensePart(false);
            const configMonthly = configPart(false);
            const periodMult = isYear ? 12 : 1;
            const rows = [];

            if (isWindows365) {
                // Licenties + configuratie are a single step here, so the
                // bundle spec is always chosen (not gated behind a later step
                // like Cloud Desktop's separate Configuratie step below).
                rows.push({
                    label: "Windows 365 licenties",
                    sub: `${qty()} × ${euro(configMonthly * periodMult)} · ${configSpecs()}`,
                    amount: euro(configMonthly * periodMult * qty()),
                });
            } else {
                rows.push({
                    label: "Gebruikerslicenties",
                    sub: `${qty()} × ${euro(licenseMonthly)}`,
                    amount: euro(licenseMonthly * periodMult * qty()),
                });

                if (current >= 2) {
                    rows.push({
                        label: "Configuratie Cloud Desktop",
                        sub: configSpecs(),
                        amount: euro(configMonthly * periodMult),
                    });
                }
            }

            if (current === maxStep) {
                const total = perLicense(isYear) * qty();
                rows.push({
                    label: "BTW",
                    sub: `${vatRate * 100}%`,
                    amount: euro(total * vatRate),
                });
            }

            el.innerHTML = rows
                .map(
                    (r) =>
                        `<div class="flex justify-between gap-4"><dt style="margin-top: 0"><span class="text-sm">${r.label}</span><span class="block text-slate-400 text-sm font-medium">${r.sub}</span></dt><dd class="text-slate-600 text-sm font-medium flex flex-col justify-end">${r.amount}</dd></div>`,
                )
                .join("");
        };

        const refresh = () => {
            const isYear = this.isYear();
            const unit = perLicense(isYear);
            const total = unit * qty();

            const totalEl = root.querySelector("[data-config-total]");
            if (totalEl) totalEl.textContent = euro(total);

            const vatAmount = total * vatRate;
            const totalInclEl = root.querySelector("[data-config-total-incl]");
            if (totalInclEl) totalInclEl.textContent = euro(total + vatAmount);

            const periodLabelEls = root.querySelectorAll("[data-period-label]");
            periodLabelEls.forEach((el) => {
                el.textContent = isYear ? "per jaar" : "per maand";
            });

            const priceMonth = root.querySelector("[data-price-month]");
            if (priceMonth) priceMonth.textContent = euro(perLicense(false));
            const priceYear = root.querySelector("[data-price-year]");
            if (priceYear) priceYear.textContent = euro(perLicense(true));

            const sumQty = root.querySelector("[data-sum-qty]");
            if (sumQty) sumQty.textContent = `${qty()} licenties`;

            // Step 1/2 cost cards — always monthly (the billing period isn't
            // chosen until step 3). Windows 365 has no license-only price
            // (see buildBreakdown above), so the step-1 card shows the
            // running total at the currently selected (default) bundle.
            const licenseMonthly = isWindows365
                ? configPart(false)
                : licensePart(false);
            const configMonthly = configPart(false);

            const licenseMath = root.querySelector("[data-license-math]");
            if (licenseMath)
                licenseMath.textContent = `${qty()} × ${euro(licenseMonthly)} per licentie / maand`;
            const licenseCost = root.querySelector("[data-license-cost]");
            if (licenseCost)
                licenseCost.textContent = `${euro(licenseMonthly * qty())} / mnd`;

            const configSpecsEl = root.querySelector("[data-config-specs]");
            if (configSpecsEl) configSpecsEl.textContent = configSpecs();
            const configCost = root.querySelector("[data-config-cost]");
            if (configCost)
                configCost.textContent = `${euro(configMonthly * (isWindows365 ? qty() : 1))} / mnd`;

            // Active quantity preset
            root.querySelectorAll("[data-qty-preset]").forEach((btn) => {
                btn.dataset.selected =
                    parseInt(btn.dataset.qtyPreset, 10) === qty()
                        ? "true"
                        : "false";
            });

            buildBreakdown();

            // Windows 365's single combined step needs its hint to carry both
            // the license math AND the chosen bundle (there's no separate
            // "Configuratie" nav item to show the latter). Its "2" hint
            // belongs to Facturatie instead, and — like Cloud Desktop's
            // Facturatie step — is left blank.
            const hint1 = root.querySelector('[data-step-hint="1"]');
            if (hint1) {
                hint1.textContent = isWindows365
                    ? `${qty()} × ${euro(licenseMonthly)} · ${configSummary()}`
                    : `${qty()} × ${euro(licenseMonthly)}`;
            }
            if (!isWindows365) {
                const hint2 = root.querySelector('[data-step-hint="2"]');
                if (hint2) hint2.textContent = configSummary();
            }

            // Bundle tiles show their own absolute price (not a "€x / unit"
            // rate), so the period toggle has to rewrite each one directly.
            if (isWindows365) {
                const resMult = isYear ? factor : 1;
                bundleTiles.forEach((tile) => {
                    const monthly = parseFloat(tile.dataset.price) || 0;
                    const label = tile.querySelector("[data-bundle-price]");
                    if (label) label.textContent = fmt(monthly * resMult);
                });
            }
        };

        // Quick-select quantity presets — reuse the qty stepper's own change
        // event so nothing else needs to know about this control.
        root.querySelectorAll("[data-qty-preset]").forEach((btn) => {
            btn.addEventListener("click", () => {
                if (!qtyInput) return;
                qtyInput.value = btn.dataset.qtyPreset;
                qtyInput.dispatchEvent(new Event("change", { bubbles: true }));
            });
        });

        qtyInput?.addEventListener("input", refresh);
        qtyInput?.addEventListener("change", refresh);
        groups.forEach((group) => {
            group
                .querySelectorAll("[data-config-tile]")
                .forEach((tile) => tile.addEventListener("click", refresh));
        });
        document.addEventListener("isource:period", refresh);

        // Windows 365: RAM/opslag only comes in fixed combinations per vCPU
        // (Microsoft's bundle tiers), unlike Cloud Desktop's independent
        // RAM/disk axes — so picking a vCPU filters the bundle tiles down to
        // the ones for that vCPU, and the hidden ram/disk inputs follow
        // whichever bundle tile is selected.
        if (isWindows365) {
            const ramInput = root.querySelector('[data-config-input="ram"]');
            const diskInput = root.querySelector('[data-config-input="disk"]');

            const selectBundle = (tile) => {
                bundleTiles.forEach((t) => (t.dataset.selected = "false"));
                tile.dataset.selected = "true";
                if (ramInput) ramInput.value = tile.dataset.ram;
                if (diskInput) diskInput.value = tile.dataset.disk;
            };

            const filterBundles = () => {
                const cpu = selectedTile(groupByKey("cpu"))?.dataset.value;
                let stillValid = false;
                bundleTiles.forEach((t) => {
                    const matches = t.dataset.cpu === cpu;
                    t.dataset.disabled = matches ? "false" : "true";
                    if (matches && t.dataset.selected === "true") {
                        stillValid = true;
                    }
                });
                if (!stillValid) {
                    const first = bundleTiles.find(
                        (t) => t.dataset.cpu === cpu,
                    );
                    if (first) selectBundle(first);
                }
            };

            bundleTiles.forEach((tile) => {
                tile.addEventListener("click", () => {
                    if (tile.dataset.disabled === "true") return;
                    selectBundle(tile);
                    refresh();
                });
            });

            groupByKey("cpu")
                ?.querySelectorAll("[data-config-tile]")
                .forEach((tile) => {
                    tile.addEventListener("click", () => {
                        filterBundles();
                        refresh();
                    });
                });

            filterBundles();
        }

        // Step navigation. Windows 365 collapses licenties + configuratie into
        // one step (see the twig), so the panel numbers only go up to 2 there
        // instead of 3 — read the actual max from the rendered panels rather
        // than hardcoding it.
        const maxStep = Math.max(
            ...panels.map((panel) => parseInt(panel.dataset.panel, 10) || 1),
        );
        let current = 1;
        const showStep = (n) => {
            current = Math.min(maxStep, Math.max(1, n));
            panels.forEach((panel) => {
                const active = parseInt(panel.dataset.panel, 10) === current;
                panel.classList.toggle("hidden", !active);
                panel.classList.toggle("flex", active);
            });
            const totalRow = root.querySelector("[data-total-row]");
            if (totalRow) {
                totalRow.classList.toggle("hidden", current !== maxStep);
                totalRow.classList.toggle("flex", current === maxStep);
            }
            steps.forEach((step) => {
                const active = parseInt(step.dataset.step, 10) === current;
                const dot = step.querySelector(".dot");
                step.classList.toggle("border-primary", active);
                step.classList.toggle("text-primary", active);
                step.classList.toggle("border-transparent", !active);
                step.classList.toggle("text-slate-500", !active);
                if (dot) {
                    dot.classList.toggle("bg-primary", active);
                    dot.classList.toggle("border-primary", active);
                    dot.classList.toggle("text-white", active);
                    dot.classList.toggle("text-slate-400", !active);
                }
            });
            if (prevBtn) prevBtn.disabled = current === 1;
            if (nextBtn) {
                nextBtn.textContent =
                    current === maxStep
                        ? "Voeg toe aan winkelwagen"
                        : "Volgende";
            }
            refresh();
        };

        steps.forEach((step) => {
            step.addEventListener("click", () =>
                showStep(parseInt(step.dataset.step, 10)),
            );
        });
        prevBtn?.addEventListener("click", () => showStep(current - 1));
        nextBtn?.addEventListener("click", (e) => {
            if (current < maxStep) {
                e.preventDefault();
                showStep(current + 1);
            }
            // On the last step the button is a real submit (name="add-to-cart")
            // — let it post normally.
        });

        showStep(1);
    }
}
